import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import Checkbox from "@/components/form/input/Checkbox";
import { expenseCategoryService } from "@/services/expenseService";
import { handleApiError } from "@/services/api";
import type { ExpenseCategory } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

export default function ExpenseCategoriesPage() {
  const isOwner = useIsOwner();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deactivateId, setDeactivateId] = useState<number | null>(null);

  const [name, setName] = useState("");
  const [isWages, setIsWages] = useState(false);

  const loadCategories = async () => {
    try {
      const data = await expenseCategoryService.list();
      setCategories((data as ExpenseCategory[]) ?? []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setIsWages(false);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (cat: ExpenseCategory) => {
    setEditing(cat);
    setName(cat.name);
    setIsWages(cat.is_wages);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editing) {
        await expenseCategoryService.update(editing.id, { name, active: editing.active, is_wages: isWages });
      } else {
        await expenseCategoryService.create({ name, is_wages: isWages });
      }
      setModalOpen(false);
      await loadCategories();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (deactivateId === null) return;
    const cat = categories.find((c) => c.id === deactivateId);
    if (!cat) return;
    try {
      await expenseCategoryService.update(cat.id, { name: cat.name, active: !cat.active });
      setDeactivateId(null);
      await loadCategories();
    } catch (err) {
      setError(handleApiError(err));
      setDeactivateId(null);
    }
  };

  const pendingCat = categories.find((c) => c.id === deactivateId);

  return (
    <>
      <PageMeta title="Expense Categories | Poultry Book" description="Manage expense categories" />
      <PageBreadcrumb pageTitle="Expense Categories" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Expense Categories
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Category
            </Button>
          )}
        </div>

        {error && !modalOpen && deactivateId === null && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : categories.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No expense categories found. Add your first category.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Created
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow
                    key={cat.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {cat.name}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge size="sm" color={cat.active ? "success" : "error"}>
                        {cat.active ? "Active" : "Inactive"}
                      </Badge>
                      {cat.is_wages && (
                        <Badge size="sm" color="info">
                          Wages
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(cat.created_at).toLocaleDateString("en-IN")}
                    </TableCell>
                    {isOwner && (
                      <TableCell className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => openEdit(cat)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeactivateId(cat.id)}
                            className={`text-sm ${cat.active ? "text-error-500 hover:text-error-600 dark:text-error-400" : "text-success-600 hover:text-success-700 dark:text-success-400"}`}
                          >
                            {cat.active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Category" : "Add Category"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="catName">Category Name *</Label>
            <Input
              id="catName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Veterinary"
              required
              maxLength={100}
            />
          </div>

          <div>
            <Checkbox
              checked={isWages}
              onChange={setIsWages}
              label="This is wages"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              When the labour register has active employees, the cash forecast
              projects payroll from it and leaves wage categories out of overheads,
              so wages are not counted twice. With no one on the register they stay in.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name.trim()}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Activate/Deactivate Confirm Modal */}
      <Modal isOpen={deactivateId !== null} onClose={() => setDeactivateId(null)} className="max-w-sm p-5 mx-4">
        <h4 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white/90">
          {pendingCat?.active ? "Deactivate" : "Activate"} Category
        </h4>
        <p className="mb-5 text-sm text-gray-600 dark:text-gray-400">
          {pendingCat?.active
            ? `Deactivate "${pendingCat?.name}"? It will no longer appear in the expense form dropdown.`
            : `Activate "${pendingCat?.name}"? It will appear in the expense form dropdown again.`}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" onClick={() => setDeactivateId(null)}>Cancel</Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleToggleActive}
            className={pendingCat?.active ? "!bg-error-500 hover:!bg-error-600" : ""}
          >
            {pendingCat?.active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      </Modal>
    </>
  );
}
